import { Injectable, OnDestroy } from '@angular/core';
import { GetProcessStatusService } from 'src/app/api-shared/process-status/get/get-process-status.service';
import JsonBanks from '../../utils/json/banks.json';
import { RedirectStep } from '../redirect-step/redirect-step';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CookieUtil } from '@utils/cookies';
import { Router } from '@angular/router';
import { NotificacionService } from '@utils/notificacion';
import { Subject } from 'rxjs';
import {
  ProcessParameters,
  RedirectStepParameters,
  NonBlockingProcessStatus,
} from 'src/app/interface/process-blocking-control-and-redirect-via-status.interface';
import { GetProcessApiResponse, GetProcessInterface } from '@interface/get-process-interface';

@Injectable({
  providedIn: 'root',
})
export class ProcessBlockingControlAndRedirectViaStatus implements OnDestroy {
  /**
   * Configuración de estados de proceso que no bloquean el flujo
   * @readonly
   */
  private readonly NON_BLOCKING_PROCESS_STATUSES: readonly NonBlockingProcessStatus[] = [
    'PENDIENTE-DE-REVISION',
    'ENVIADO-AL-BANCO-PARA-CREACION',
    'DECEVAL-EMITIDO-Y-RECIBIDO',
    'DECEVAL-ENVIADO-AL-CLIENTE',
  ] as const;

  /**
   * Subject para manejo de destrucción de observables
   * @private
   */
  private readonly destroy$ = new Subject<void>();

  /**
   * Referencia al modal activo para control de memoria
   * @private
   */
  private activeModalRef?: NgbModalRef;

  /**
   * Información de bancos (lazy loading)
   * @private
   */
  private _banksData?: typeof JsonBanks;
  constructor(
    public cookies_util: CookieUtil,
    private notifications_components: NotificacionService,
    private get_process_information: GetProcessStatusService,
    private _redirectStep: RedirectStep,
    private router: Router,
  ) {}

  public validateCookieProcessAndIdToken() {
    const { id_process, id_token } = this.getUserCredentials();

    // Validación de autenticación
    if (!this.isUserAuthenticated(id_token)) {
      return this.redirectToLogin();
    }

    // Validación de proceso activo
    if (!this.hasActiveProcess(id_process)) {
      return this.redirectToProcessList();
    }

    return false;
  }

  /**
   * Getter para acceso lazy a datos de bancos
   * @returns Datos de bancos
   */
  private get banksData(): typeof JsonBanks {
    if (!this._banksData) {
      this._banksData = JsonBanks;
    }
    return this._banksData;
  }

  /**
   * Implementación de OnDestroy para limpieza de recursos
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeActiveModal();
  }

  /**
   * Cierra el modal activo si existe para prevenir memory leaks
   * @private
   */
  private closeActiveModal(): void {
    if (this.activeModalRef) {
      this.activeModalRef.close();
      this.activeModalRef = undefined;
    }
  }

  /**
   * Controla el bloqueo de procesos y maneja redirecciones basadas en el estado del proceso.
   * Valida la autenticación del usuario, el estado del proceso y redirige según las reglas de negocio.
   *
   * @param objParameters - Parámetros que contienen información del proceso y banco
   * @param objParameters.process_info - Información del proceso (opcional)
   * @param objParameters.bank_id - ID del banco (opcional)
   * @param objParameters.status_search - Estados de proceso permitidos (opcional)
   *
   * @throws {Error} Cuando falla la validación del token o proceso
   *
   * @example
   * ```typescript
   * const params = {
   *   process_info: { id: '123', process_status: 'LEAD-SIN-FORMULARIO' },
   *   bank_id: 1,
   *   status_search: ['LEAD-SIN-FORMULARIO', 'DATOS-PERSONALES']
   * };
   * await processBlockingService.ProcessBlockingAndRedirect(params);
   * ```
   */
  public async ProcessBlockingAndRedirect(objParameters: ProcessParameters): Promise<void> {
    try {
      const { process_info, bank_id, step } = objParameters;
      const { id_process, id_token } = this.getUserCredentials();

      // Validación de autenticación
      if (!this.isUserAuthenticated(id_token)) {
        await this.redirectToLogin();
        return;
      }

      // Validación de proceso activo
      if (!this.hasActiveProcess(id_process)) {
        await this.redirectToProcessList();
        return;
      }

      // Usar información del proceso si está disponible
      if (this.isValidProcessInfo(process_info)) {
        await this.executeRedirectFlow({
          body: process_info!,
          objParamaters: objParameters,
          bank_id,
        });
        return;
      }

      // Obtener información del proceso desde el backend
      await this.fetchAndProcessStatus(id_process!, id_token!, objParameters, bank_id);
    } catch (error) {
      console.error('Error en ProcessBlockingAndRedirect:', error);
      this.handleProcessError(error);
    }
  }

  /**
   * Obtiene las credenciales del usuario desde las cookies
   * @returns Objeto con id_process e id_token
   * @private
   */
  private getUserCredentials(): { id_process: string | null; id_token: string | null } {
    return {
      id_process: this.cookies_util.get('id_process'),
      id_token: this.cookies_util.get('id_token_user'),
    };
  }

  /**
   * Verifica si el usuario está autenticado
   * @param id_token - Token de autenticación
   * @returns true si el usuario está autenticado
   * @private
   */
  private isUserAuthenticated(id_token: string | null): boolean {
    return Boolean(id_token);
  }

  /**
   * Verifica si hay un proceso activo
   * @param id_process - ID del proceso
   * @returns true si hay un proceso activo
   * @private
   */
  private hasActiveProcess(id_process: string | null): boolean {
    return Boolean(id_process);
  }

  /**
   * Valida si la información del proceso es válida
   * @param process_info - Información del proceso
   * @returns true si la información es válida
   * @private
   */
  private isValidProcessInfo(process_info?: GetProcessInterface): boolean {
    return Boolean(process_info) && Object.keys(process_info || {}).length > 0;
  }

  /**
   * Redirige al usuario a la página de login
   * @private
   */
  private async redirectToLogin(): Promise<void> {
    await this.router.navigate(['/portal/login']);
  }

  /**
   * Redirige al usuario a la lista de procesos
   * @private
   */
  private async redirectToProcessList(): Promise<void> {
    await this.router.navigate(['/mis-procesos']);
  }

  /**
   * Ejecuta el flujo de redirección con la información del proceso
   * @param params - Parámetros del flujo de redirección
   * @private
   */
  private async executeRedirectFlow(params: {
    body: GetProcessInterface;
    objParamaters: ProcessParameters;
    bank_id?: number;
  }): Promise<void> {
    return this.coreRediredctStep(params);
  }

  /**
   * Obtiene y procesa el estado del proceso desde el backend
   * @param id_process - ID del proceso
   * @param id_token - Token de autenticación
   * @param objParameters - Parámetros originales
   * @param bank_id - ID del banco
   * @private
   */
  private async fetchAndProcessStatus(
    id_process: string,
    id_token: string,
    objParameters: ProcessParameters,
    bank_id?: number,
  ): Promise<void> {
    try {
      const data = (await this.get_process_information.GET_PROCESS_STATUS(
        id_process,
        id_token,
      )) as GetProcessApiResponse;

      if (this.isSuccessfulResponse(data.status) && this.isValidProcessInfo(data.body)) {
        await this.executeRedirectFlow({
          body: data.body,
          objParamaters: objParameters,
          bank_id,
        });
      }
    } catch (error) {
      console.error('Error al obtener estado del proceso:', error);
      throw error;
    }
  }

  /**
   * Verifica si la respuesta HTTP es exitosa
   * @param status - Código de estado HTTP
   * @returns true si la respuesta es exitosa
   * @private
   */
  private isSuccessfulResponse(status: number): boolean {
    return [200, 201, 204].includes(status);
  }

  /**
   * Maneja errores del proceso
   * @param error - Error ocurrido
   * @private
   */
  private handleProcessError(error: any): void {
    // Implementar lógica de manejo de errores según las necesidades del negocio
    console.error('Error en el proceso:', error);
    // Posible redirección a página de error o notificación al usuario
  }

  /**
   * Núcleo de la lógica de redirección basada en el estado del proceso.
   * Maneja los diferentes escenarios de redirección según el estado y configuración del proceso.
   *
   * @param response - Respuesta que contiene información del proceso y parámetros
   * @param response.body - Información del proceso desde el backend
   * @param response.objParamaters - Parámetros originales de la solicitud
   * @param response.bank_id - ID del banco (opcional)
   *
   * @throws {Error} Cuando falla la obtención del nombre del banco
   *
   * @example
   * ```typescript
   * const response = {
   *   body: { process_status: 'LEAD-SIN-FORMULARIO', bank_id: 1, extra: {} },
   *   objParamaters: { status_search: ['LEAD-SIN-FORMULARIO'] },
   *   bank_id: 1
   * };
   * await service.coreRediredctStep(response);
   * ```
   */
  async coreRediredctStep(response: {
    body: GetProcessInterface;
    objParamaters: ProcessParameters;
    bank_id?: number;
  }): Promise<void> {
    try {
      // Verificar bloqueo de proceso
      if (this.shouldBlockProcess(response.body)) {
        await this.router.navigate(['/no-podemos-continuar-con-tu-proceso']);
        return;
      }

      const bankName = this.getBankName(response.body.bank_id);
      const redirectParams = this.buildRedirectParameters(response.body, bankName);

      // Determinar tipo de redirección
      if (this.shouldRedirectToCurrentStep(response)) {
        await this.handleStepRedirection(redirectParams, true);
      } else if (this.shouldRedirectToBankMismatch(response)) {
        await this.handleBankMismatchRedirection(response.body, bankName);
      } else {
        await this.handleStepRedirection(redirectParams, false);
      }
    } catch (error) {
      console.error('Error en coreRediredctStep:', error);
      throw error;
    }
  }

    /**
   * Determina si el proceso debe ser bloqueado
   * @param processInfo - Información del proceso
   * @returns true si el proceso debe ser bloqueado
   * @private
   */
  private shouldBlockProcess(processInfo: GetProcessInterface): boolean {
    const shouldBlockByBlockProcess = Boolean(processInfo.extra?.block_process);
    
    // Solo bloquear por continue_wl si existe y es explícitamente false
    const shouldBlockByContinueWl = processInfo.extra?.continue_wl === false;
    
    return (
      (shouldBlockByBlockProcess || shouldBlockByContinueWl) &&
      !this.NON_BLOCKING_PROCESS_STATUSES.includes(processInfo.process_status as any)
    );
  }

  /**
   * Obtiene el nombre del banco por su ID
   * @param bankId - ID del banco
   * @returns Nombre del banco
   * @throws {Error} Si no se encuentra el banco
   * @private
   */
  private getBankName(bankId?: number): string {
    if (!bankId) {
      throw new Error('Bank ID is required');
    }

    const bankData = this.banksData['raw'][bankId];
    if (!bankData) {
      throw new Error(`Bank with ID ${bankId} not found`);
    }

    return bankData['bank'];
  }

  /**
   * Construye los parámetros de redirección
   * @param processInfo - Información del proceso
   * @param bankName - Nombre del banco
   * @returns Parámetros de redirección
   * @private
   */
  private buildRedirectParameters(processInfo: GetProcessInterface, bankName: string): RedirectStepParameters {
    return {
      name_bank: bankName,
      process_status: processInfo.process_status || '',
      id_bank: processInfo.bank_id || 0,
      required_identity_validation: Boolean(
        processInfo.extra?.required_identity_validation && processInfo.identity_validation === null,
      ),
      extra: processInfo.extra || {},
      data_truora: processInfo.identity_validation?.data,
      id_process: processInfo.id,
      amount: processInfo.amount,
      term: processInfo.term,
      rate: processInfo.rate,
      validate_rate_changing: true,
    };
  }

  /**
   * Determina si debe redirigir al paso actual
   * @param response - Respuesta del proceso
   * @returns true si debe redirigir al paso actual
   * @private
   */
  private shouldRedirectToCurrentStep(response: {
    body: GetProcessInterface;
    objParamaters: ProcessParameters;
  }): boolean {
    const statusSearch = response.objParamaters.status_search || [];
    const currentStatus = response.body.process_status || '';

    return statusSearch.filter((status) => status.includes(currentStatus)).length === 0;
  }

  /**
   * Determina si hay un conflicto de banco
   * @param response - Respuesta del proceso
   * @returns true si hay conflicto de banco
   * @private
   */
  private shouldRedirectToBankMismatch(response: {
    body: GetProcessInterface;
    objParamaters: ProcessParameters;
    bank_id?: number;
  }): boolean {
    return Boolean(response.bank_id) && response.bank_id !== response.body.bank_id;
  }

  /**
   * Maneja la redirección de paso
   * @param redirectParams - Parámetros de redirección
   * @param isRedirect - Indica si es una redirección
   * @private
   */
  private async handleStepRedirection(redirectParams: RedirectStepParameters, isRedirect: boolean): Promise<void> {
    this.notificationRedirectOtherPage(isRedirect);
    await this._redirectStep._RedirectStep(redirectParams);
  }

  /**
   * Maneja la redirección por conflicto de banco
   * @param processInfo - Información del proceso
   * @param bankName - Nombre del banco
   * @private
   */
  private async handleBankMismatchRedirection(processInfo: GetProcessInterface, bankName: string): Promise<void> {
    this.notificationRedirectOtherPage(true);

    const redirectParams: RedirectStepParameters = {
      ...this.buildRedirectParameters(processInfo, bankName),
      process_status: 'LEAD-SIN-FORMULARIO', // Forzar a lead sin formulario en caso de conflicto
    };

    await this._redirectStep._RedirectStep(redirectParams);
  }

  /**
   * Envía notificación sobre redirección a otra página.
   * Utiliza el servicio de notificaciones para comunicar cambios de estado en la navegación.
   *
   * @param isRedirect - Indica si ocurrió una redirección
   *   - `true`: El usuario fue redirigido a un paso diferente al esperado
   *   - `false`: El usuario permaneció en el flujo normal
   *
   * @example
   * ```typescript
   * // Notificar redirección por paso incorrecto
   * service.notificationRedirectOtherPage(true);
   *
   * // Notificar flujo normal
   * service.notificationRedirectOtherPage(false);
   * ```
   *
   * @see {@link NotificacionService.sendNotificacion} Para más detalles sobre el servicio de notificaciones
   */
  notificationRedirectOtherPage(isRedirect: boolean): void {
    try {
      this.notifications_components.sendNotificacion({
        api: 'send_notification_redirect_other_page',
        redirect: isRedirect,
      });
    } catch (error) {
      console.error('Error al enviar notificación de redirección:', error);
      // No relanzar el error para no interrumpir el flujo principal
    }
  }
}
